import type {
  PlaybookId,
  PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import type { PlaybookNodeSampleToolResultSectionAggregates } from "@repo/app-types/entities/playbookNodeSampleToolResult";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useState } from "react";
import { useLazyFetchSampleToolResultGradingsQuery } from "../../api/entities/playbook.js";
import type { SampleToolResultSectionEntry } from "../../hooks/useSampleToolResults.js";
import type { GradedCallsTab } from "./gradedCallsSlideover.js";
import { SectionApplicabilityComparisonChart } from "./sectionApplicabilityComparisonChart.js";
import { SectionGradedCallsSlideover } from "./sectionGradedCallsSlideover.js";
import { SectionHealthCheckCard } from "./sectionHealthCheckCard.js";

interface SectionSuggestionResultsProps {
  /**
   * Undefined when no saved sample-tool result exists for the section yet —
   * the pane then renders the empty placeholder so the column stays present.
   */
  aggregates: PlaybookNodeSampleToolResultSectionAggregates | undefined;
  /** The full sample tool result entry, used to fetch grading details. */
  suggestion?: SampleToolResultSectionEntry;
  playbookId?: PlaybookId | null;
  playbookNodeId: PlaybookNodeId;
}

export const SectionSuggestionResults: FC<SectionSuggestionResultsProps> = ({
  aggregates,
  suggestion,
  playbookId,
}) => {
  const [isSlideoverOpen, setIsSlideoverOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<GradedCallsTab>("sample");
  const [fetchGradings, { data: gradingsData, isFetching, isError }] =
    useLazyFetchSampleToolResultGradingsQuery();

  const openGradedCalls = (tab: GradedCallsTab) => {
    if (playbookId && suggestion) {
      void fetchGradings({
        playbookId,
        sampleToolResultId: suggestion.id,
      });
    }
    setSelectedTab(tab);
    setIsSlideoverOpen(true);
  };

  if (!aggregates) {
    return (
      <div className="flex min-h-[160px] flex-1 items-center justify-center text-center">
        <p className="max-w-[220px] text-xs text-muted-foreground">
          The test results of your section will appear here.
        </p>
      </div>
    );
  }

  const hasPlaybookContext =
    suggestion !== undefined && playbookId !== undefined && playbookId !== null;

  const hasFeedbackGrades =
    isNotNullNotUndefined(suggestion?.health_check) &&
    suggestion.health_check.matches_team_feedback.status !== "not_applicable";

  const hasSampleGrades = aggregates.sample_size > 0;

  const showGradedCallsButton =
    hasPlaybookContext && (hasSampleGrades || hasFeedbackGrades);

  const onViewFeedback =
    hasPlaybookContext && hasFeedbackGrades
      ? () => openGradedCalls("feedback")
      : undefined;

  return (
    <>
      <div className="space-y-4">
        {suggestion?.health_check ? (
          <SectionHealthCheckCard
            healthCheck={suggestion.health_check}
            onViewFeedback={onViewFeedback}
          />
        ) : null}

        <div className="rounded-md bg-white px-4 pt-3 pb-4 shadow-sm ring-1 ring-black/5">
          <SectionApplicabilityComparisonChart
            baseline={aggregates.baseline}
            tested={aggregates.tested}
          />
        </div>

        {showGradedCallsButton ? (
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.MEDIUM}
            onClick={() => openGradedCalls("sample")}
            className="w-full justify-center"
          >
            See graded calls
          </Button>
        ) : null}
      </div>

      <SectionGradedCallsSlideover
        isOpen={isSlideoverOpen}
        onClose={() => setIsSlideoverOpen(false)}
        selectedTab={selectedTab}
        onSelectTab={setSelectedTab}
        gradings={
          gradingsData?.node_type === "section" ? gradingsData : undefined
        }
        isLoading={isFetching}
        isError={isError}
      />
    </>
  );
};
